import {
  CARD_ACTIONS_BY_TAB,
  CRITICAL_POPULATIONS_SLUG,
  DEFAULT_VIEW,
  STATUS_TO_TAB,
  TAB_ORDER,
  VIEW_QUERY_KEY,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import type {
  CaptureCounts,
  CaptureListTab,
  CriticalPopulationsView,
  DecisionActionKey,
  DimensionDefinition,
  GroupedCapture,
  OpenDecisionPanel,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';

import type {
  CriticalPopulationDimensionDefinitionResponse,
  CriticalPopulationDimensionDto,
  CriticalPopulationResponse,
} from 'api/generated/schemas';

import { formatDate } from 'helpers/dates-formatter';

const DISPLAY_DATE_FORMAT = 'MMM D, YYYY';

export const groupByTab = (
  rows: CriticalPopulationResponse[] = []
): GroupedCapture => {
  const grouped: GroupedCapture = { active: [], evaluating: [], deferred: [] };

  rows.forEach((row) => {
    const tab = row.status ? STATUS_TO_TAB[row.status] : undefined;

    if (tab) grouped[tab].push(row);
  });

  return grouped;
};

export const getTabCounts = (grouped: GroupedCapture): CaptureCounts => ({
  active: grouped.active.length,
  evaluating: grouped.evaluating.length,
  deferred: grouped.deferred.length,
});

export const isCaptureListTab = (
  tab: CriticalPopulationsView
): tab is CaptureListTab => tab !== 'configuration';

export const sortDimensions = (
  dimensions: CriticalPopulationDimensionDto[] = []
): CriticalPopulationDimensionDto[] =>
  [...dimensions].sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));

const toValueSetsByAttribute = (
  dimensions: { attribute?: string; values?: string[] }[]
): Map<string, Set<string>> =>
  new Map(
    dimensions.flatMap(({ attribute, values = [] }) =>
      attribute ? [[attribute, new Set(values)] as const] : []
    )
  );

export const haveDimensionsChanged = (
  original: { attribute?: string; values?: string[] }[] = [],
  current: { attribute?: string; values?: string[] }[] = []
): boolean => {
  const before = toValueSetsByAttribute(original);
  const after = toValueSetsByAttribute(current);

  if (before.size !== after.size) return true;

  return [...before].some(([attribute, values]) => {
    const next = after.get(attribute);

    return (
      !next ||
      next.size !== values.size ||
      [...values].some((v) => !next.has(v))
    );
  });
};

export const formatDimensionSubtitle = (
  dimensions: CriticalPopulationDimensionDto[] = []
): string =>
  sortDimensions(dimensions)
    .map(
      ({ displayName, attribute, values = [] }) =>
        `${displayName ?? attribute ?? ''}: ${values.join(', ')}`
    )
    .join(' · ');

export const formatDisplayDate = (date: string | undefined): string =>
  formatDate(date, DISPLAY_DATE_FORMAT) ?? '';

export const normalizeCatalog = (
  catalog: CriticalPopulationDimensionDefinitionResponse[] = []
): DimensionDefinition[] =>
  catalog.flatMap(({ attribute, displayName, singleSelect, values }) =>
    attribute
      ? [
          {
            attribute,
            displayName: displayName ?? attribute,
            singleSelect: !!singleSelect,
            values: values ?? [],
          },
        ]
      : []
  );

export const getYearFromPath = (pathname: string): number | null => {
  const parts = pathname.split('/');
  const slugIndex = parts.indexOf(CRITICAL_POPULATIONS_SLUG);
  const rawYear = slugIndex === -1 ? undefined : parts[slugIndex + 1];
  const year = rawYear ? Number.parseInt(rawYear, 10) : Number.NaN;

  return Number.isNaN(year) ? null : year;
};

export const getExplicitViewFromSearch = (
  search: string
): CriticalPopulationsView | null => {
  const view = new URLSearchParams(search).get(VIEW_QUERY_KEY);

  return TAB_ORDER.find((tab) => tab === view) ?? null;
};

export const getViewFromSearch = (search: string): CriticalPopulationsView =>
  getExplicitViewFromSearch(search) ?? DEFAULT_VIEW;

export const shouldLandOnEvaluating = (
  explicitView: CriticalPopulationsView | null,
  counts: CaptureCounts
): boolean => !explicitView && !counts.active;

export const buildTabPath = (
  clientId: string | number,
  year: number,
  view: CriticalPopulationsView,
  keepDefaultView = false
): string => {
  const path = `/admin/clients/${clientId}/${CRITICAL_POPULATIONS_SLUG}/${year}`;

  return view === DEFAULT_VIEW && !keepDefaultView
    ? path
    : `${path}?${VIEW_QUERY_KEY}=${view}`;
};

export const getCardOpenAction = (
  openPanel: OpenDecisionPanel | null,
  rowId: number | undefined,
  tab: CaptureListTab
): DecisionActionKey | null => {
  if (!openPanel || openPanel.id !== rowId) return null;

  const cardActions: readonly string[] = CARD_ACTIONS_BY_TAB[tab];

  return cardActions.includes(openPanel.action) ? openPanel.action : null;
};

export const getTabCountLabel = (
  count: number | undefined
): number | undefined => count || undefined;
